import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Plus, RefreshCw } from 'lucide-react';
import { policiesApi, Policy, PolicyStats } from '../api/policies.api';
import { useAuth } from '@contexts/AuthContext';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  active: 'bg-green-100 text-green-800',
  retired: 'bg-red-100 text-red-800',
};

const CATEGORIES = ['', 'information_security', 'data_privacy', 'operational', 'hr', 'financial', 'environmental', 'governance'];

const PolicyLibrary: React.FC = () => {
  const { user } = useAuth();
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [stats, setStats] = useState<PolicyStats | null>(null);
  const [pending, setPending] = useState<Policy[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selected, setSelected] = useState<Policy | null>(null);
  const [createModal, setCreateModal] = useState(false);
  const [form, setForm] = useState({ title: '', category: 'information_security', content: '', review_due_at: '' });
  const [submitting, setSubmitting] = useState(false);

  const companyId = user?.company_id ?? undefined;

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const [list, statsRes, pend] = await Promise.all([
        policiesApi.list({ ...(statusFilter ? { status: statusFilter } : {}), ...(categoryFilter ? { category: categoryFilter } : {}), ...(companyId !== undefined ? { company_id: companyId } : {}) }),
        policiesApi.getStats(companyId),
        policiesApi.getPendingAcknowledgements(),
      ]);
      setPolicies((list as any).policies || []);
      setStats(statsRes);
      setPending(pend);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, categoryFilter, companyId]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.title.trim() || !companyId) return;
    setSubmitting(true);
    try {
      await policiesApi.create({ company_id: companyId, ...form });
      setCreateModal(false);
      setForm({ title: '', category: 'information_security', content: '', review_due_at: '' });
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (id: number) => {
    try { await policiesApi.approve(id); if (selected?.id === id) setSelected(null); load(); }
    catch (e: any) { setError(e.message); }
  };

  const handleRetire = async (id: number) => {
    try { await policiesApi.retire(id); if (selected?.id === id) setSelected(null); load(); }
    catch (e: any) { setError(e.message); }
  };

  const handleAcknowledge = async (id: number) => {
    try { await policiesApi.acknowledge(id); load(); }
    catch (e: any) { setError(e.message); }
  };

  const reviewOverdue = (p: Policy) => p.review_due_at && new Date(p.review_due_at) < new Date();

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Policy Library</h1>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
            <RefreshCw size={16} /> Refresh
          </button>
          <button onClick={() => setCreateModal(true)} className="flex items-center gap-1 px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
            <Plus size={16} /> New Policy
          </button>
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

      {pending.length > 0 && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl flex items-start gap-3">
          <AlertTriangle size={18} className="text-yellow-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-yellow-800">You have {pending.length} policy acknowledgement(s) pending</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {pending.map(p => (
                <button key={p.id} onClick={() => handleAcknowledge(p.id)}
                  className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded border border-yellow-300 hover:bg-yellow-200">
                  Acknowledge: {p.title}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Total', value: stats.total },
            { label: 'Draft', value: stats.draft },
            { label: 'Active', value: stats.active },
            { label: 'Pending Ack', value: stats.pending_acknowledgement },
            { label: 'Due Review', value: stats.due_for_review },
          ].map(({ label, value }) => (
            <div key={label} className="bg-white border rounded-xl p-4">
              <p className="text-sm text-gray-500 mb-1">{label}</p>
              <p className="text-2xl font-bold text-gray-900">{value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-3">
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          {['', 'draft', 'active', 'retired'].map(s => <option key={s} value={s}>{s || 'All Statuses'}</option>)}
        </select>
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          {CATEGORIES.map(c => <option key={c} value={c}>{c ? c.replace('_', ' ') : 'All Categories'}</option>)}
        </select>
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
        ) : policies.length === 0 ? (
          <div className="py-12 text-gray-500">No policies found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>{['Title', 'Category', 'Version', 'Review Due', 'Status', 'Actions'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y">
              {policies.map(p => (
                <tr key={p.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelected(p)}>
                  <td className="px-4 py-3 text-left font-medium text-gray-900">{p.title}</td>
                  <td className="px-4 py-3 text-left">
                    <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">{p.category.replace('_', ' ')}</span>
                  </td>
                  <td className="px-4 py-3 text-left text-gray-600">{p.version}</td>
                  <td className={`px-4 py-3 ${reviewOverdue(p) ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                    {p.review_due_at ? new Date(p.review_due_at).toLocaleDateString() : '-'}
                    {reviewOverdue(p) && ' ⚠️'}
                  </td>
                  <td className="px-4 py-3 text-left">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[p.status]}`}>{p.status}</span>
                  </td>
                  <td className="px-4 py-3 text-left flex gap-2" onClick={e => e.stopPropagation()}>
                    {p.status === 'draft' && (
                      <button onClick={() => handleApprove(p.id)} className="text-green-600 hover:underline text-xs">Approve</button>
                    )}
                    {p.status === 'active' && (
                      <>
                        <button onClick={() => handleAcknowledge(p.id)} className="text-blue-600 hover:underline text-xs">Acknowledge</button>
                        <button onClick={() => handleRetire(p.id)} className="text-red-500 hover:underline text-xs">Retire</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl space-y-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">{selected.title}</h2>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="flex gap-2 flex-wrap">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[selected.status]}`}>{selected.status}</span>
              <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">{selected.category}</span>
              <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700">v{selected.version}</span>
            </div>
            {selected.content && <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-700 whitespace-pre-wrap">{selected.content}</div>}
            <div className="flex gap-3 justify-end pt-2">
              {selected.status === 'draft' && (
                <button onClick={() => handleApprove(selected.id)} className="px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700">
                  Approve Policy
                </button>
              )}
              {selected.status === 'active' && (
                <>
                  <button onClick={() => handleAcknowledge(selected.id)} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                    Acknowledge
                  </button>
                  <button onClick={() => handleRetire(selected.id)} className="px-4 py-2 text-sm text-white bg-red-500 rounded-lg hover:bg-red-600">
                    Retire
                  </button>
                </>
              )}
              <button onClick={() => setSelected(null)} className="px-4 py-2 text-sm border rounded-lg">Close</button>
            </div>
          </div>
        </div>
      )}

      {createModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg space-y-4">
            <h2 className="text-lg font-semibold">New Policy</h2>
            <input placeholder="Title *" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" />
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm">
              {CATEGORIES.filter(Boolean).map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
            </select>
            <textarea placeholder="Policy content..." value={form.content} onChange={e => setForm({ ...form, content: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" rows={6} />
            <div>
              <label className="text-xs text-gray-500 block mb-1">Review Due Date</label>
              <input type="date" value={form.review_due_at} onChange={e => setForm({ ...form, review_due_at: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setCreateModal(false)} className="px-4 py-2 text-sm border rounded-lg">Cancel</button>
              <button onClick={handleCreate} disabled={submitting || !form.title.trim()}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {submitting ? 'Creating...' : 'Create Policy'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PolicyLibrary;
